import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, ChevronDown } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/motion";
import FinishSwatch from "@/components/FinishSwatch";
import CategoryIcon from "@/components/CategoryIcon";
import { ProductCard } from "@/pages/Home";
import { useSite } from "@/content";

const SITE_TITLE = "SanCub — Architectural Tapware & Bathroom Fixtures | Where Form Meets Flow";
const slug = (f) => f.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const BLURBS = {
  Tapware: "Basin mixers, sink taps and pull-outs in Chrome, Matt Black, Gunmetal and Brushed Gold.",
  Showers: "Rail showers, rain heads, mixers, arms and outlets — every finish matched across the range.",
  Bathtubs: "Back-to-wall and freestanding stone-composite baths, fluted or classic.",
  Toilets: "Wall-faced rimless suites in gloss and matte, with matching flush buttons.",
  Basins: "Countertop and art basins — gloss ceramics through to travertine-look statement pieces.",
  Sinks: "Granite, fireclay farmhouse and handmade SS304 stainless steel kitchen sinks.",
  Vanities: "Fluted and curved timber-look vanities with sintered stone benchtops, 45–150cm.",
  "LED Mirrors": "Arch, pill and rectangular mirrors and cabinets with frameless or framed LED lighting.",
  Accessories: "Robe hooks, towel bars, paper holders, drains and wastes to finish the room.",
};

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold transition duration-300 ${
      active
        ? "bg-ink text-white"
        : "bg-white text-ink/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
    }`}
  >
    {children}
  </button>
);

const FilterGroup = ({ label, options, value, onChange, swatches }) => (
  <div className="flex items-center gap-3" data-testid={`filter-group-${slug(label)}`}>
    <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.18em] text-ink/40">{label}</span>
    <div className="no-scrollbar flex flex-nowrap gap-2 overflow-x-auto pb-1">
      <Chip active={value === "All"} onClick={() => onChange("All")} testId={`filter-${slug(label)}-all`}>
        All
      </Chip>
      {options.map((o) => (
        <Chip key={o} active={value === o} onClick={() => onChange(o)} testId={`filter-${slug(label)}-${slug(o)}`}>
          {swatches && <FinishSwatch finish={o} size="h-3 w-3" />}
          {o}
        </Chip>
      ))}
    </div>
  </div>
);

const CategoryPage = ({ category }) => {
  const { products } = useSite();
  const all = useMemo(() => products.filter((p) => p.category === category), [products, category]);
  const [type, setType] = useState("All");
  const [range, setRange] = useState("All");
  const [finish, setFinish] = useState("All");
  const [size, setSize] = useState("All");
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (!openMenu) return undefined;
    const close = (e) => {
      if (!e.target.closest("[data-mfilter]")) setOpenMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenu]);

  useEffect(() => {
    setType("All");
    setRange("All");
    setFinish("All");
    setSize("All");
    setOpenMenu(null);
    document.title = `${category} | SanCub`;
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.content;
    if (desc) desc.content = `Shop SanCub ${category.toLowerCase()} — ${BLURBS[category]} 100% Australian owned & operated. Where form meets flow.`;
    window.scrollTo(0, 0);
    return () => {
      document.title = SITE_TITLE;
      if (desc && prev) desc.content = prev;
    };
  }, [category]);

  const types = useMemo(() => [...new Set(all.map((p) => p.type).filter(Boolean))], [all]);
  const ranges = useMemo(() => [...new Set(all.map((p) => p.name.split(" ")[0]))].sort(), [all]);
  const finishes = useMemo(() => [...new Set(all.flatMap((p) => p.finishes))], [all]);
  const sizes = useMemo(
    () =>
      [...new Set(all.map((p) => (p.name.match(/(\d{3,4})\s*mm/) || [])[1]).filter(Boolean))]
        .map((s) => `${s}mm`)
        .sort((a, b) => parseInt(a) - parseInt(b)),
    [all]
  );

  const filtered = all.filter(
    (p) =>
      (type === "All" || p.type === type) &&
      (range === "All" || p.name.startsWith(range)) &&
      (finish === "All" || p.finishes.includes(finish)) &&
      (size === "All" || p.name.includes(size))
  );

  const groups = [
    { key: "type", label: "Type", options: types, value: type, set: setType },
    { key: "finish", label: "Finish", options: finishes, value: finish, set: setFinish, swatches: true },
    { key: "size", label: "Size", options: sizes, value: size, set: setSize },
    { key: "range", label: "Range", options: ranges, value: range, set: setRange },
  ].filter((g) => g.options.length > 1);
  const activeGroup = groups.find((g) => g.key === openMenu);

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-44" data-testid={`category-page-${slug(category)}`}>
      <FadeUp>
        <p className="flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-ink shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <CategoryIcon category={category} className="h-4.5 h-5 w-5" />
          </span>
          SanCub Collections
        </p>
      </FadeUp>
      <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className="font-display text-6xl font-medium leading-[1.02] tracking-tight text-ink md:text-8xl" data-testid="category-headline">
          <MaskedLine>{category}</MaskedLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="category-count">
            {filtered.length} of {all.length} {all.length === 1 ? "product" : "products"}
          </p>
        </FadeUp>
      </div>
      <FadeUp delay={0.25} className="mt-6 max-w-xl">
        <p className="text-base leading-relaxed text-ink/70 md:text-lg">{BLURBS[category]}</p>
      </FadeUp>

      <FadeUp delay={0.3} className="mt-12">
        {/* Mobile: compact dropdown pills */}
        <div className="relative md:hidden" data-mfilter data-testid="category-filters-mobile">
          <div className="no-scrollbar -mx-6 flex gap-2 overflow-x-auto px-6 pb-1">
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setOpenMenu(openMenu === g.key ? null : g.key)}
                aria-expanded={openMenu === g.key}
                data-testid={`mfilter-${g.key}`}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[12px] font-semibold transition duration-300 ${
                  g.value !== "All" || openMenu === g.key
                    ? "bg-ink text-white"
                    : "bg-white text-ink/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                }`}
              >
                <span className="max-w-[150px] truncate">{g.value === "All" ? g.label : `${g.label}: ${g.value}`}</span>
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-300 ${openMenu === g.key ? "rotate-180" : ""}`} />
              </button>
            ))}
          </div>
          <AnimatePresence>
            {activeGroup && (
              <motion.div
                key={activeGroup.key}
                initial={{ opacity: 0, y: 8, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.99 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute inset-x-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-3xl bg-white p-1.5 shadow-[0_24px_60px_rgb(0,0,0,0.14)]"
                data-testid="mfilter-menu"
              >
                {["All", ...activeGroup.options].map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => { activeGroup.set(o); setOpenMenu(null); }}
                    data-testid={`mfilter-option-${slug(o)}`}
                    className={`flex w-full items-center gap-2.5 rounded-2xl px-4 py-3 text-left text-[13px] transition-colors duration-200 ${
                      activeGroup.value === o ? "bg-sand font-semibold text-ink" : "font-medium text-ink/70 hover:bg-sand/60"
                    }`}
                  >
                    <span className="flex w-4 shrink-0 justify-center">
                      {activeGroup.value === o && <Check className="h-3.5 w-3.5" />}
                    </span>
                    {activeGroup.swatches && o !== "All" && <FinishSwatch finish={o} size="h-3 w-3" />}
                    {o === "All" ? `All ${activeGroup.label.toLowerCase()}s` : o}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop: grouped filter card */}
        <div className="hidden flex-col gap-4 rounded-[1.5rem] bg-white/60 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:flex md:flex-row md:flex-wrap md:items-center md:gap-x-10 md:gap-y-4 md:p-6" data-testid="category-filters">
          {types.length > 1 && (
            <FilterGroup label="Type" options={types} value={type} onChange={setType} />
          )}
          {ranges.length > 1 && (
            <FilterGroup label="Range" options={ranges} value={range} onChange={setRange} />
          )}
          {finishes.length > 1 && (
            <FilterGroup label="Finish" options={finishes} value={finish} onChange={setFinish} swatches />
          )}
          {sizes.length > 1 && (
            <FilterGroup label="Size" options={sizes} value={size} onChange={setSize} />
          )}
        </div>
      </FadeUp>

      <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-grid">
        {filtered.map((p, i) => (
          <FadeUp key={p.id} delay={Math.min(i, 6) * 0.05}>
            <ProductCard p={p} />
          </FadeUp>
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div className="mt-16 rounded-[2rem] bg-white py-20 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="category-empty">
          <p className="font-display text-2xl font-medium text-ink/50">Nothing matches those filters yet.</p>
          <button
            onClick={() => { setType("All"); setRange("All"); setFinish("All"); setSize("All"); }}
            data-testid="filter-reset"
            className="mt-5 rounded-full bg-ink px-7 py-3 text-[13px] font-semibold text-white"
          >
            Reset filters
          </button>
        </div>
      )}

      <FadeUp className="mt-24">
        <div className="flex flex-col items-start gap-8 rounded-[3rem] bg-white p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:flex-row md:items-center md:justify-between md:p-14">
          <p className="max-w-lg font-display text-3xl font-medium leading-[1.15] tracking-tight text-ink md:text-4xl">
            Trade, wholesale or a full fit-out — send us your schedule.
          </p>
          <Link
            to="/contact"
            data-testid="category-enquire-link"
            className="group flex shrink-0 items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
          >
            Enquire now
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </FadeUp>
    </main>
  );
};

export default CategoryPage;
